import { Component, computed, effect, ElementRef, inject, signal, untracked, viewChild } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { RaBadgeComponent, RaShinyTextComponent, RaSkeletonComponent, RaIconComponent } from 'ra-ui';
import {
    RaChatBubbleComponent,
    RaChatComposerComponent,
    RaChatMessageListComponent,
    RaChatOrbComponent,
} from 'src/app/shared/components/ra-chat';
import { AiEditChange } from '@domain';
import { AiEditKickoffService } from 'src/app/shared/services/ai-edit-kickoff.service';
import { EditDataService } from '../../services/_edit-data.service';
import { AiEditMessage, AiEditStore, attentionChanges } from '../../services/ai-edit.store';
import { AiEditChecklistComponent } from './ai-edit-checklist/ai-edit-checklist.component';

/** Sugestões do chat vazio (chaves i18n): os pedidos mais comuns, a um clique. */
const STARTER_KEYS = [
    'document.edit.aiEdit.starters.summarize',
    'document.edit.aiEdit.starters.formal',
    'document.edit.aiEdit.starters.addSection',
];

/**
 * Coluna do chat no modo "Editar com IA" (docs/EDITOR-CHAT-IA.md, seções 2 e 3).
 *
 * A UI nunca fica muda: cada etapa do pedido tem o seu sinal aqui (bolha otimista →
 * texto brilhante da etapa com esqueletos → checklist das alterações → resposta com
 * chips) e outro na prévia ao lado. Nada de overlay bloqueante.
 */
@Component({
    selector: 'ai-chat-panel',
    templateUrl: 'ai-chat-panel.component.html',
    host: {
        '(dragenter)': 'onDragEnter($event)',
        '(dragover)': 'onDragOver($event)',
        '(dragleave)': 'onDragLeave($event)',
        '(drop)': 'onDrop($event)',
        '(paste)': 'onPaste($event)',
    },
    styleUrl: 'ai-chat-panel.component.scss',
    imports: [
        RaChatMessageListComponent,
        RaChatBubbleComponent,
        RaChatComposerComponent,
        RaChatOrbComponent,
        RaShinyTextComponent,
        RaSkeletonComponent,
        RaBadgeComponent,
        RaIconComponent,
        AiEditChecklistComponent,
        TranslatePipe,
    ],
})
export class AiChatPanelComponent {
    public store = inject(AiEditStore);
    private _editData = inject(EditDataService);
    private _kickoff = inject(AiEditKickoffService);

    constructor() {
        // Documento criado pelo wizard "Criar com IA": a descrição escrita lá é a primeira
        // mensagem. Espera o histórico carregar e o chat estar livre, e envia uma vez.
        effect(() => {
            const documentId = this._editData.document()?._id;
            const ready = !this.store.restoring() && !this.store.busy() && !this.store.uploading();
            if (!ready || !this._kickoff.peek(documentId)) return;

            untracked(() => {
                const text = this._kickoff.take(documentId);
                if (text) this.store.send(text);
            });
        });
    }

    public title = computed(() => this._editData.document()?.title ?? '');
    public starterKeys = STARTER_KEYS;

    /** Chips iniciais só com a conversa vazia: depois quem sugere é a IA. */
    public showStarters = computed(() => !this.store.messages().length && !this.store.busy());

    public sendStarter(text: string): void {
        this.store.send(text);
    }

    // ───────────────────────────────────── conflitos e falhas (estados 5 e 6)

    /** Alterações de um pedido que esperam o usuário: viram cartões abaixo do checklist. */
    public attention(message: AiEditMessage): AiEditChange[] {
        return message.reverted ? [] : attentionChanges(message.changes);
    }

    /** Cartões de conflito com as duas versões abertas por inteiro ("Ver os dois"). */
    public expanded = signal(new Set<string>());

    public toggleExpanded(changeId: string): void {
        this.expanded.update(ids => {
            const next = new Set(ids);
            if (next.has(changeId)) next.delete(changeId);
            else next.add(changeId);
            return next;
        });
    }

    /** A inicial de quem editou, quando não há avatar. */
    public initial(name: string | undefined): string {
        return (name ?? '').trim().charAt(0).toUpperCase() || '?';
    }

    // ─────────────────────────────────────────── anexos (botão, arrastar, colar)

    private _fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');
    /** Arrastando arquivos por cima do chat: a área de soltar aparece. */
    public dragging = signal(false);
    /** dragenter/dragleave disparam a cada filho cruzado: o contador evita o pisca-pisca. */
    private _dragDepth = 0;

    public hasReadyAttachment = computed(() => this.store.attachments().some(a => a.status === 'done'));

    public openFilePicker(): void {
        this._fileInput()?.nativeElement.click();
    }

    public onFilesPicked(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files?.length) this.store.addFiles(input.files);
        input.value = '';
    }

    public onDragEnter(event: DragEvent): void {
        if (!hasFiles(event)) return;
        event.preventDefault();
        this._dragDepth++;
        this.dragging.set(true);
    }

    public onDragOver(event: DragEvent): void {
        if (!hasFiles(event)) return;
        // Sem isto o navegador abre o arquivo na aba em vez de soltar aqui.
        event.preventDefault();
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
    }

    public onDragLeave(event: DragEvent): void {
        if (!hasFiles(event)) return;
        this._dragDepth = Math.max(0, this._dragDepth - 1);
        if (!this._dragDepth) this.dragging.set(false);
    }

    public onDrop(event: DragEvent): void {
        if (!hasFiles(event)) return;
        event.preventDefault();
        this._dragDepth = 0;
        this.dragging.set(false);
        if (event.dataTransfer?.files?.length) this.store.addFiles(event.dataTransfer.files);
    }

    /** Colar um print ou uma foto copiada também anexa. */
    public onPaste(event: ClipboardEvent): void {
        const files = Array.from(event.clipboardData?.files ?? []).filter(
            file => file.type.startsWith('image/') || file.type.startsWith('video/')
        );
        if (!files.length) return;
        event.preventDefault();
        this.store.addFiles(files);
    }
}

/** O arraste traz ARQUIVOS (e não texto ou um link da página)? */
function hasFiles(event: DragEvent): boolean {
    return Array.from(event.dataTransfer?.types ?? []).includes('Files');
}
